import { useTranslation } from "react-i18next";
import Button from "@/shared/ui/components/button";
import { PerksBanner } from "@/modules/plan-selection/components/perks-banner";
import { type AppContext, type PaymentOption } from "@/types";
import { logError } from "@/services/logging/logger";
import { type PpsPaymentOption } from "@/modules/checkout-session";
import { Processing } from "@/modules/plan-selection/views/plan-selection-view";

function toPpsPaymentOption(paymentOption: PaymentOption): PpsPaymentOption {
  return {
    type: paymentOption.tenure > 0 ? "PAY_BY_INSTALMENTS" : "PAY_NOW",
    payments: paymentOption.tenure,
  };
}

interface StickyFooterContentProps {
  ctx: AppContext;
  cashbackData: {
    eligible: boolean;
    cashbackAmt: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    appliedRewards: any;
    waivedOffReason?: string | null;
    waivedOffFeeAmount?: number;
    processingFeeAmount?: number;
    isFeeWaivedOff?: boolean;
  };
  selectedPaymentOption: PpsPaymentOption | undefined;
  processing: number;
  onContinue: (option?: PpsPaymentOption) => Promise<void>;
  payInFullJustClicked: boolean;
  payNextMonthJustClicked: boolean;
}

export function StickyFooterContent({
  ctx,
  cashbackData,
  selectedPaymentOption,
  processing,
  onContinue,
  payInFullJustClicked,
  payNextMonthJustClicked,
}: StickyFooterContentProps) {
  const { t } = useTranslation();

  const {
    eligible,
    cashbackAmt,
    appliedRewards,
    waivedOffReason,
    waivedOffFeeAmount = 0,
    processingFeeAmount = 0,
    isFeeWaivedOff = false,
  } = cashbackData;
  const paymentOptions = ctx.session?.paymentOptions ?? [];
  const hasPaymentOptions = paymentOptions.length > 0;
  const hasInstalments = paymentOptions.some((opt) => opt.tenure >= 2);

  const findDefaultInstallmentOption = (): PaymentOption | undefined => {
    const preSelectedInstalment = paymentOptions.find(
      (opt) => opt.tenure >= 2 && opt.preSelected === true,
    );
    return preSelectedInstalment
      ? preSelectedInstalment
      : paymentOptions.find((opt) => opt.tenure >= 2);
  };

  const getOptionToContinue = (): PpsPaymentOption => {
    const type = selectedPaymentOption?.type;

    // If user just clicked PAY_NOW or PAY_NEXT_MONTH, use their selection
    const wasJustClicked =
      (type === "PAY_NEXT_MONTH" && payNextMonthJustClicked) ||
      (type === "PAY_NOW" && payInFullJustClicked);

    if (wasJustClicked && selectedPaymentOption) {
      return selectedPaymentOption;
    }

    // If previously selected option is PAY_NOW or PAY_NEXT_MONTH
    const isNonInstallmentType =
      type === "PAY_NEXT_MONTH" || type === "PAY_NOW";

    if (isNonInstallmentType && !hasInstalments) {
      return selectedPaymentOption!;
    }

    if (isNonInstallmentType && hasInstalments) {
      const defaultInstallment = findDefaultInstallmentOption();
      return defaultInstallment
        ? {
            type: "PAY_BY_INSTALMENTS",
            payments: defaultInstallment.tenure,
          }
        : selectedPaymentOption!;
    }
    // If selected option is already a valid installment (>= 2 payments)
    if (selectedPaymentOption && selectedPaymentOption.payments >= 2) {
      return selectedPaymentOption;
    }
    // Try to find a default installment option
    const defaultInstallment = findDefaultInstallmentOption();
    if (defaultInstallment) {
      return {
        type: "PAY_BY_INSTALMENTS",
        payments: defaultInstallment.tenure,
      };
    }

    // Last resort: use first available option or selected option
    return paymentOptions.length > 0
      ? toPpsPaymentOption(paymentOptions[0])
      : selectedPaymentOption!;
  };

  const handleClick = () => {
    const optionToUse = getOptionToContinue();

    onContinue(optionToUse).catch((error) => {
      logError(
        "payment-plan-selection",
        "Error continuing to payment",
        error instanceof Error ? error : new Error(String(error)),
        { error },
      );
    });
  };

  const declinedOffers = ctx.session?.declinedOffers ?? [];
  const declinedOffersWithSubvention = declinedOffers.filter(
    (d: Record<string, unknown>) =>
      d.subvention != null && typeof d.subvention === "object",
  );

  const isSelectedOptionDeclined =
    selectedPaymentOption?.type === "PAY_BY_INSTALMENTS" &&
    declinedOffersWithSubvention.some(
      (d: Record<string, unknown>) =>
        (typeof d.tenure === "number" &&
          d.tenure === selectedPaymentOption?.payments) ||
        (typeof d.offerId === "string" &&
          ctx.session?.paymentOptions?.some(
            (opt) =>
              opt.tenure === selectedPaymentOption?.payments &&
              opt.offerId === d.offerId,
          )),
    );

  const isPayInFullOrPayNextMonth =
    selectedPaymentOption?.type === "PAY_NOW" ||
    selectedPaymentOption?.type === "PAY_NEXT_MONTH";

  return (
    <>
      {!isPayInFullOrPayNextMonth && (
        <PerksBanner
          ctx={ctx}
          cashbackAmount={cashbackAmt}
          cashbackEligible={eligible}
          appliedRewards={appliedRewards}
          waivedOffReason={waivedOffReason}
          waivedOffFeeAmount={waivedOffFeeAmount}
          processingFeeAmount={processingFeeAmount}
          isFeeWaivedOff={isFeeWaivedOff}
          disabled={processing !== Processing.NONE}
        />
      )}

      {hasPaymentOptions && (
        <Button
          size="large"
          onClick={handleClick}
          loading={processing === Processing.PAYMENT_OPTION}
          disabled={processing !== Processing.NONE || isSelectedOptionDeclined}>
          <span className="capitalize">{t("common.continue")}</span>
        </Button>
      )}
    </>
  );
}
